import { motion } from "framer-motion";
import { Store } from "@/models/storeModel";
import { HeroSection } from "./HeroSection";
import MenuSection from "./MenuSection";
import ServicesSection from "./ServicesSection";
import { TestimonialSection } from "./TestimonialSection";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Instagram,
  Twitter,
  Youtube,
  Edit3,
  Check,
  X,
  Clock,
  Globe,
  Copy,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/authStore";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

interface StorefrontContent {
  hero: {
    title: string;
    subtitle: string;
    buttonText: string;
    buttonAction: string;
    backgroundImage?: string;
    images?: string[];
  };
  colors: {
    background: string;
    primary: string;
    secondary: string;
    accent: string;
    text: string;
  };
  testimonials?: Array<{
    id: string;
    name: string;
    review: string;
    rating: number;
    role?: string;
  }>;
}

interface StorefrontPreviewProps {
  store: Store;
  content: StorefrontContent;
  isEditing: boolean;
  isPublished?: boolean;
  onPublish?: (slug: string) => void;
  onContentChange?: (content: Partial<StorefrontContent>) => void;
}

interface OpeningHours {
  [key: string]: {
    open: string;
    close: string;
    closed: boolean;
  };
}

const defaultHours: OpeningHours = {
  monday: { open: "09:00", close: "17:00", closed: false },
  tuesday: { open: "09:00", close: "17:00", closed: false },
  wednesday: { open: "09:00", close: "17:00", closed: false },
  thursday: { open: "09:00", close: "17:00", closed: false },
  friday: { open: "09:00", close: "17:00", closed: false },
  saturday: { open: "10:00", close: "15:00", closed: false },
  sunday: { open: "10:00", close: "15:00", closed: true },
};

export function StorefrontPreview({
  store,
  content,
  isEditing,
  isPublished = false,
  onPublish,
  onContentChange,
}: StorefrontPreviewProps) {
  const { isAuthenticated } = useAuthStore();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [contactInfo, setContactInfo] = useState({
    address: store.address || "",
    phone: store.phone || "",
    email: store.email || "",
  });

  const [openingHours, setOpeningHours] = useState<OpeningHours>(
    store.business_hours || defaultHours
  );

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [tempContactInfo, setTempContactInfo] = useState(contactInfo);
  const [tempOpeningHours, setTempOpeningHours] = useState(openingHours);
  const [showPublishedBanner, setShowPublishedBanner] = useState(isPublished);

  useEffect(() => {
    if (isPublished && showPublishedBanner) {
      const timer = setTimeout(() => {
        setShowPublishedBanner(false);
      }, 60000); // 1 minute

      return () => clearTimeout(timer);
    }
  }, [isPublished, showPublishedBanner]);

  const handleButtonClick = (action: string) => {
    if (action.startsWith("#")) {
      const element = document.getElementById(action.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else if (action.startsWith("http")) {
      window.open(action, "_blank");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "send-storefront-contact",
        {
          body: {
            store_id: store.id,
            customer_name: formData.name,
            customer_email: formData.email,
            message: formData.message,
          },
        }
      );

      if (error) {
        console.error("Error submitting contact form:", error);
        throw error;
      }

      toast({
        title: "Message sent successfully!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });

      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Failed to submit contact form:", error);
      toast({
        title: "Failed to send message",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleContactSave = () => {
    setContactInfo(tempContactInfo);
    setIsEditingContact(false);
  };

  const handleContactCancel = () => {
    setTempContactInfo(contactInfo);
    setIsEditingContact(false);
  };

  const handleHoursSave = () => {
    setOpeningHours(tempOpeningHours);
    setIsEditingHours(false);
  };

  const handleHoursCancel = () => {
    setTempOpeningHours(openingHours);
    setIsEditingHours(false);
  };

  const handleHoursChange = (
    day: string,
    field: string,
    value: string | boolean
  ) => {
    setTempOpeningHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleTestimonialsChange = (testimonials: any[]) => {
    if (onContentChange) {
      onContentChange({ testimonials });
    }
  };

  const getDayName = (day: string) => {
    const dayNames: { [key: string]: string } = {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    };
    return dayNames[day] || day;
  };

  const copyStorefrontUrl = () => {
    if (store.slug) {
      const url = `${window.location.origin}/store/${store.slug}`;
      navigator.clipboard.writeText(url);
      toast({
        title: "URL Copied!",
        description: "Storefront URL has been copied to clipboard",
      });
    }
  };

  const dismissBanner = () => {
    setShowPublishedBanner(false);
  };

  // Determine if user can edit - authenticated users can always edit their own storefront
  const canEdit = isAuthenticated;
  const showEditingUI = canEdit && isEditing;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: content.colors.background }}
    >
      {/* Published Banner - only show to authenticated users */}
      {isPublished && showPublishedBanner && canEdit && (
        <div className="fixed top-16 left-0 right-0 z-50 bg-green-600 text-white">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  {t("storefront.published.success")}
                </span>
                <code className="bg-green-700 px-2 py-1 rounded text-xs font-mono">
                  {window.location.origin}/storefront/{store.slug}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyStorefrontUrl}
                  className="text-white hover:bg-green-700 h-7 px-2"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  {t("storefront.published.copy")}
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissBanner}
                className="text-white hover:bg-green-700 h-7 px-2"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={`${canEdit ? "pt-24" : ""}`}>
        {/* Hero Section */}
        <HeroSection
          title={content.hero.title}
          subtitle={content.hero.subtitle}
          buttonText={content.hero.buttonText}
          onButtonClick={() => handleButtonClick(content.hero.buttonAction)}
          backgroundImage={content.hero.backgroundImage}
          images={content.hero.images || []}
          colors={content.colors}
          isEditing={showEditingUI}
        />

        {/* Menu Section (for FnB stores) */}
        <div id="menu">
          <MenuSection
            storeId={store.id}
            currency="USD"
            colors={content.colors}
          />
        </div>

        {/* Testimonial Section */}
        <TestimonialSection
          colors={content.colors}
          isEditing={showEditingUI}
          testimonials={content.testimonials || []}
          onTestimonialsChange={handleTestimonialsChange}
        />

        {/* Contact Section */}
        <section
          id="contact"
          className="py-20 relative overflow-hidden"
          style={{ backgroundColor: "#f8fafc" }}
        >
          <div className="container mx-auto px-4 relative max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                {t("storefront.contact.title")}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t("storefront.contact.subtitle")}
              </p>
            </motion.div>

            {/* Improved Layout - Two Column Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Left Column - Contact Info & Hours */}
              <div className="xl:col-span-1 space-y-8">
                {/* Contact Information */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {t("storefront.contact.info.title")}
                    </h3>
                    {showEditingUI && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (isEditingContact) {
                            handleContactCancel();
                          } else {
                            setIsEditingContact(true);
                          }
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {isEditingContact ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <Edit3 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{
                          backgroundColor: `${content.colors.primary}15`,
                        }}
                      >
                        <MapPin
                          className="w-4 h-4"
                          style={{ color: content.colors.primary }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium mb-1 text-gray-900 text-sm">
                          {t("storefront.contact.info.address")}
                        </h4>
                        {isEditingContact ? (
                          <Input
                            value={tempContactInfo.address}
                            onChange={(e) =>
                              setTempContactInfo({
                                ...tempContactInfo,
                                address: e.target.value,
                              })
                            }
                            className="text-xs"
                            placeholder={t(
                              "storefront.contact.info.enterAddress"
                            )}
                          />
                        ) : (
                          <p className="text-xs text-gray-600 break-words">
                            {contactInfo.address ||
                              t("storefront.contact.info.addressNotProvided")}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{
                          backgroundColor: `${content.colors.primary}15`,
                        }}
                      >
                        <Phone
                          className="w-4 h-4"
                          style={{ color: content.colors.primary }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium mb-1 text-gray-900 text-sm">
                          {t("storefront.contact.info.phone")}
                        </h4>
                        {isEditingContact ? (
                          <Input
                            value={tempContactInfo.phone}
                            onChange={(e) =>
                              setTempContactInfo({
                                ...tempContactInfo,
                                phone: e.target.value,
                              })
                            }
                            className="text-xs"
                            placeholder={t(
                              "storefront.contact.info.enterPhone"
                            )}
                          />
                        ) : (
                          <p className="text-xs text-gray-600">
                            {contactInfo.phone ||
                              t("storefront.contact.info.phoneNotProvided")}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{
                          backgroundColor: `${content.colors.primary}15`,
                        }}
                      >
                        <Mail
                          className="w-4 h-4"
                          style={{ color: content.colors.primary }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium mb-1 text-gray-900 text-sm">
                          {t("storefront.contact.info.email")}
                        </h4>
                        {isEditingContact ? (
                          <Input
                            value={tempContactInfo.email}
                            onChange={(e) =>
                              setTempContactInfo({
                                ...tempContactInfo,
                                email: e.target.value,
                              })
                            }
                            className="text-xs"
                            placeholder={t(
                              "storefront.contact.info.enterEmail"
                            )}
                          />
                        ) : (
                          <p className="text-xs text-gray-600 break-all">
                            {contactInfo.email ||
                              t("storefront.contact.info.emailNotProvided")}
                          </p>
                        )}
                      </div>
                    </div>

                    {isEditingContact && (
                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={handleContactSave}
                          size="sm"
                          style={{ backgroundColor: content.colors.primary }}
                          className="text-white text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          {t("storefront.contact.info.save")}
                        </Button>
                        <Button
                          onClick={handleContactCancel}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          {t("storefront.contact.info.cancel")}
                        </Button>
                      </div>
                    )}

                    {/* Social Media Links */}
                    <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{
                          backgroundColor: `${content.colors.primary}15`,
                        }}
                      >
                        <Instagram
                          className="w-4 h-4"
                          style={{ color: content.colors.primary }}
                        />
                      </div>
                      <div>
                        <h4 className="font-medium mb-2 text-gray-900 text-sm">
                          {t("storefront.contact.social.followUs")}
                        </h4>
                        <div className="flex space-x-2">
                          <a
                            href="#"
                            className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110"
                            style={{
                              backgroundColor: `${content.colors.primary}15`,
                            }}
                            aria-label="Instagram"
                          >
                            <Instagram
                              className="w-4 h-4"
                              style={{ color: content.colors.primary }}
                            />
                          </a>
                          <a
                            href="#"
                            className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110"
                            style={{
                              backgroundColor: `${content.colors.primary}15`,
                            }}
                            aria-label="Twitter"
                          >
                            <Twitter
                              className="w-4 h-4"
                              style={{ color: content.colors.primary }}
                            />
                          </a>
                          <a
                            href="#"
                            className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110"
                            style={{
                              backgroundColor: `${content.colors.primary}15`,
                            }}
                            aria-label="YouTube"
                          >
                            <Youtube
                              className="w-4 h-4"
                              style={{ color: content.colors.primary }}
                            />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Opening Hours */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                      <div
                        className="p-1.5 rounded-lg"
                        style={{
                          backgroundColor: `${content.colors.primary}15`,
                        }}
                      >
                        <Clock
                          className="w-4 h-4"
                          style={{ color: content.colors.primary }}
                        />
                      </div>
                      {t("storefront.contact.hours.title")}
                    </h3>
                    {showEditingUI && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (isEditingHours) {
                            handleHoursCancel();
                          } else {
                            setIsEditingHours(true);
                          }
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {isEditingHours ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <Edit3 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {Object.entries(
                      isEditingHours ? tempOpeningHours : openingHours
                    ).map(([day, hours]) => (
                      <div
                        key={day}
                        className="flex items-center justify-between py-2 border-b border-gray-50 last:border-b-0"
                      >
                        <div className="font-medium text-gray-700 capitalize text-sm min-w-[80px]">
                          {getDayName(day)}
                        </div>

                        {isEditingHours ? (
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={!tempOpeningHours[day].closed}
                                onChange={(e) =>
                                  handleHoursChange(
                                    day,
                                    "closed",
                                    !e.target.checked
                                  )
                                }
                                className="rounded border-gray-300"
                              />
                              <span className="text-xs text-gray-600">
                                {t("storefront.contact.hours.open")}
                              </span>
                            </label>

                            {!tempOpeningHours[day].closed && (
                              <>
                                <Input
                                  type="time"
                                  value={tempOpeningHours[day].open}
                                  onChange={(e) =>
                                    handleHoursChange(
                                      day,
                                      "open",
                                      e.target.value
                                    )
                                  }
                                  className="w-16 text-xs h-8"
                                />
                                <span className="text-gray-400 text-xs">
                                  to
                                </span>
                                <Input
                                  type="time"
                                  value={tempOpeningHours[day].close}
                                  onChange={(e) =>
                                    handleHoursChange(
                                      day,
                                      "close",
                                      e.target.value
                                    )
                                  }
                                  className="w-16 text-xs h-8"
                                />
                              </>
                            )}

                            {tempOpeningHours[day].closed && (
                              <span className="text-red-500 font-medium text-xs">
                                {t("storefront.contact.hours.closed")}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-right">
                            {hours.closed ? (
                              <span className="text-red-500 font-medium text-xs">
                                Closed
                              </span>
                            ) : (
                              <div className="text-gray-600 text-xs">
                                <span className="font-mono">{hours.open}</span>
                                <span className="mx-1">-</span>
                                <span className="font-mono">{hours.close}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {isEditingHours && (
                      <div className="flex gap-2 pt-3 border-t border-gray-100">
                        <Button
                          onClick={handleHoursSave}
                          size="sm"
                          style={{ backgroundColor: content.colors.primary }}
                          className="text-white text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Save Hours
                        </Button>
                        <Button
                          onClick={handleHoursCancel}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Right Column - Contact Form */}
              <div className="xl:col-span-2">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="h-full"
                >
                  <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full"
                  >
                    <h3 className="text-2xl font-semibold mb-8 text-gray-900">
                      {t("storefront.contact.form.title")}
                    </h3>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("storefront.contact.form.name")}
                          </label>
                          <Input
                            type="text"
                            placeholder={t(
                              "storefront.contact.form.namePlaceholder"
                            )}
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            className="bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("storefront.contact.form.email")}
                          </label>
                          <Input
                            type="email"
                            placeholder={t(
                              "storefront.contact.form.emailPlaceholder"
                            )}
                            value={formData.email}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                email: e.target.value,
                              })
                            }
                            className="bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t("storefront.contact.form.message")}
                        </label>
                        <Textarea
                          placeholder={t(
                            "storefront.contact.form.messagePlaceholder"
                          )}
                          value={formData.message}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              message: e.target.value,
                            })
                          }
                          className="bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl min-h-[160px] resize-none"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full text-white rounded-xl py-3 text-base font-medium hover:scale-[1.02] transition-all duration-200"
                        style={{ backgroundColor: content.colors.primary }}
                      >
                        <Send className="w-5 h-5 mr-2" />
                        {t("storefront.contact.form.send")}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              </div>
            </div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-20 pt-8 border-t border-gray-200 text-center"
            >
              <p className="text-sm text-gray-500">
                {t("storefront.footer.copyright", {
                  year: new Date().getFullYear(),
                  storeName: "Xerven",
                })}
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
}
